import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import ReactTooltip from 'react-tooltip'

import RecessInlineNote from '../shared/RecessInlineNote'
import { SubmissionChecklist } from './submit_application_package/SubmissionChecklist'
import { ProcessorEmailList } from './submit_application_package/ProcessorEmailList'
import { WithdrawalFlow } from './submit_application_package/WithdrawalFlow'
import { ReadyToSubmitModal } from './submit_application_package/ReadyToSubmitModal'
import { SubmissionSuccessModal } from './submit_application_package/SubmissionSuccessModal'
import { SubmissionFailedModal } from './submit_application_package/SubmissionFailedModal'
import { SubmissionErrorModal } from './submit_application_package/SubmissionErrorModal'
import { useChecklistState } from './submit_application_package/useChecklistState'
import { usePackageSubmission } from './submit_application_package/usePackageSubmission'
import { promptMissingInformation } from './submit_application_package/packageSubmissionPrompts'
import SubmissionHoursDropdown from './submit_application_package/SubmissionHoursDropdown'
import { TodayStatus } from './submit_application_package/TodayStatus'
import axios from '../../lib/axios'

const ROOT_ID = 'submit-application-package'

const submissionSuccessKey = (packageHashedId) => `bopa:submission-success:${packageHashedId}`

const outerCardStyle = {
  padding: 18,
  borderRadius: 6,
  background: 'var(--white, #FFF)',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.10)',
}

const submitAreaStyle = {
  flexShrink: 0,
  width: 227,
  height: 'fit-content',
  padding: '12px',
  background: 'var(--fill, #F9F9F9)',
  borderRadius: 6,
  border: '1px solid var(--accent, #F0F0F0)',
}

const DEFAULT_LABELS = {
  tag: 'Package',
  processorTag: 'Application',
  submitButton: 'submit',
  reSubmitButton: 're-submit',
  withdrawButton: 'withdraw application',
}

// Root for the redesigned submit/withdraw card. Replaces ApplyStateButton end-to-end.
// Props are computed server-side (see _invite_management_table.html.haml) so the
// component itself is free of jQuery globals and locale lookups.
export function SubmitApplicationPackage({
  packageHashedId,
  submitDisabled = false,
  submittedToManagingAgent = false,
  unrevokable = false,
  onlyProcessingDue = false,
  incompleteBuyersData = null,
  applicantStatus = '',
  checklist = [],
  processorEmails = [],
  labels: labelsProp = {},
  preSubmitModal = null,
  showWithdraw = false,
  reSubmit = false,
  processorWorking = false,
  submissionModals = null,
  roundsOn = false,
  scheduleByDay,
  todayStatus,
  nextTransitionAt,
  withinSubmissionWindow,
  propertyPageUrl,
  showSubmissionHours,
  decisionPhase = false,
  summerRecessNote = null,
}) {
  const labels = { ...DEFAULT_LABELS, ...(labelsProp || {}) }
  const rootRef = useRef(null)
  const items = useChecklistState(rootRef, checklist)
  const { submitted, revoked, apply, revoke } = usePackageSubmission({
    packageHashedId,
    submittedToManagingAgent,
    unrevokable,
  })
  const [withinWindow, setWithinWindow] = useState(withinSubmissionWindow)
  const [today, setToday] = useState(todayStatus)
  const [nextTransition, setNextTransition] = useState(nextTransitionAt)
  const [activeModal, setActiveModal] = useState(null)
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [modal, setModal] = useState(null)
  const [genericErrors, setGenericErrors] = useState(null)

  const closeModal = () => {
    setModal(null)
    setGenericErrors(null)
  }

  const refreshBusinessHoursStatus = async () => {
    try {
      const { data } = await axios.get(`/packages/${packageHashedId}/business_hours_status`)
      setWithinWindow(data.withinSubmissionWindow)
      setToday(data.todayStatus)
      setNextTransition(data.nextTransitionAt)
    } catch (_err) {
      // retain existing state on network error
    }
  }

  useEffect(() => {
    if (!nextTransition) return

    const delay = new Date(nextTransition).getTime() - Date.now()
    const timerId = setTimeout(refreshBusinessHoursStatus, Math.max(0, delay))

    return () => clearTimeout(timerId)
  }, [nextTransition, packageHashedId])

  useEffect(() => {
    try {
      if (sessionStorage.getItem(submissionSuccessKey(packageHashedId))) {
        sessionStorage.removeItem(submissionSuccessKey(packageHashedId))
        setModal('success')
      }
    } catch (_err) {
      // sessionStorage unavailable (private mode / disabled) — skip the post-reload modal.
    }
  }, [packageHashedId])

  const performSubmit = () => {
    if (justSubmitted) return

    setJustSubmitted(true)
    apply()
      .then((data) => {
        if (data && data.redirect) return

        try {
          sessionStorage.setItem(submissionSuccessKey(packageHashedId), '1')
        } catch (_err) {
          // sessionStorage unavailable — reload still fixes the stale UI; the modal is best-effort.
        }
        window.location.reload()
      })
      .catch((err) => {
        setJustSubmitted(false)
        const key = err && err.error
        const feeError =
          key === 'immediate_fees_failed_to_collect' || key === 'immediate_fees_not_authorized'
        if (feeError && submissionModals?.hasImmediateFees) {
          setModal('failed')
        } else {
          setGenericErrors(err && err.errors ? err.errors : null)
          setModal('error')
        }
      })
  }

  const handleSubmit = () => {
    if (incompleteBuyersData) {
      promptMissingInformation({ packageHashedId, tag: labels.tag })
      return
    }

    setModal('ready')
  }

  const isSubmitted = submitted
  const isUnrevokable = revoked && unrevokable
  const allComplete = items.length > 0 && items.every((i) => i.complete)
  const outsideSubmissionWindow = showSubmissionHours && !withinWindow
  const isSubmitDisabled = submitDisabled || !allComplete || justSubmitted || (isSubmitted && processorWorking) || outsideSubmissionWindow
  const showWithdrawTrigger = (showWithdraw || justSubmitted) && isSubmitted && !isUnrevokable
  const showSubmitTooltip = (submitDisabled || outsideSubmissionWindow) && (!roundsOn || !processorWorking)

  return (
    <div
      id={ROOT_ID}
      ref={rootRef}
      className="submit-application-package"
      data-active-modal={activeModal || undefined}
      style={outerCardStyle}
    >
      <div className="dome-d-flex dome-justify-between dome-gap18">
        <div className="dome-d-flex dome-flex-column" style={{ flexGrow: 1, minWidth: 0 }}>
          <span className="dome-h4 dome-color-darkest-grey dome-text-w500">submit application package</span>
          <span className="dome-p2 dome-color-dark-grey">
            Application package initiators and agents can submit once:
          </span>
          <SubmissionChecklist items={items} />
          <ProcessorEmailList
            emails={processorEmails}
            submitted={isSubmitted}
            applicantStatus={applicantStatus}
            processorTag={labels.processorTag}
          />
        </div>

        <div className="dome-d-flex dome-flex-column dome-gap12" style={submitAreaStyle}>
          {decisionPhase ? (
            <div className="dome-d-flex dome-align-center dome-flex-column dome-gap6">
              <span className="dome-p2 dome-color-darkest-grey dome-text-w500">no resubmission required</span>
              <span className="dome-p2 dome-color-med-grey" style={{ textAlign: 'center' }}>
                Applications under board review do not require resubmissions.
              </span>
            </div>
          ) : (
            <>
              {showSubmissionHours ? (
                <div className="dome-d-flex dome-align-center dome-flex-column dome-gap6">
                  <SubmissionHoursDropdown
                    scheduleByDay={scheduleByDay}
                    propertyPageUrl={propertyPageUrl}
                    panelStyle={{
                      background: 'white',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  />
                  <TodayStatus
                    active={withinWindow}
                    dayName={today?.day_name}
                    opensAt={today?.opens_at}
                    closesAt={today?.closes_at}
                    timezone_short={today?.timezone_short}
                  />
                </div>
              ) : (
                <div className="dome-d-flex dome-align-center dome-flex-column">
                  <span className="dome-p2 dome-color-darkest-grey dome-text-w500">to submit:</span>
                  <span className="dome-p2 dome-color-med-grey" style={{ textAlign: 'center' }}>
                    Make sure all items on the left are complete to submit.
                  </span>
                </div>
              )}

              {summerRecessNote && (
                <RecessInlineNote label={summerRecessNote.label} tooltip={summerRecessNote.tooltip} />
              )}

              <button
                type="button"
                disabled={isSubmitDisabled}
                data-tip
                data-for="submitApplicationPackageBtn"
                onClick={handleSubmit}
                className={clsx('dome-btn dome-btn-base', isSubmitDisabled ? 'dome-btn-disabled' : 'dome-btn-dark-green')}
              >
                {reSubmit ? labels.reSubmitButton : labels.submitButton}
              </button>

              <ReactTooltip
                disable={!showSubmitTooltip}
                id="submitApplicationPackageBtn"
                place="bottom"
                effect="solid"
                type="light"
                arrowColor="transparent"
              >
                <div className="dome-d-flex dome-flex-column">
                  <span className="dome-p2 dome-color-darkest-grey">why can&apos;t I submit? </span>
                  {showSubmissionHours && !withinWindow ? (
                    <span className="dome-p3 dome-text-w300">Submissions are only accepted during business hours.</span>
                  ) : (
                    <span className="dome-p3 dome-text-w300">You may submit after completing listed tasks.</span>
                  )}
                </div>
              </ReactTooltip>

              {showWithdrawTrigger && (
                <WithdrawalFlow onSubmit={revoke} withdrawText={labels.withdrawButton} labels={labels} />
              )}
            </>
          )}
        </div>
      </div>

      <ReadyToSubmitModal
        isOpen={modal === 'ready'}
        roundsOn={roundsOn}
        hasImmediateFees={submissionModals?.hasImmediateFees}
        immediateFeesName={submissionModals?.immediateFeesName}
        immediateFeesDescriptor={submissionModals?.immediateFeesDescriptor}
        attestationSubline={submissionModals?.attestationSubline}
        isSubmitting={justSubmitted}
        onBack={closeModal}
        onConfirm={performSubmit}
      />
      <SubmissionSuccessModal
        isOpen={modal === 'success'}
        labels={labels}
        hasImmediateFees={submissionModals?.hasImmediateFees}
        immediateFeesName={submissionModals?.immediateFeesName}
        timelineSteps={submissionModals?.timelineSteps || []}
        packageHashedId={packageHashedId}
        onClose={closeModal}
      />
      <SubmissionFailedModal
        isOpen={modal === 'failed'}
        immediateFeesName={submissionModals?.immediateFeesName}
        onClose={closeModal}
      />
      <SubmissionErrorModal
        isOpen={modal === 'error'}
        errors={genericErrors}
        onClose={closeModal}
      />
    </div>
  )
}

export default SubmitApplicationPackage
